import { COLUMNS_HIDDEN_BY_DEFAULT } from 'common/constants/analyst-dashboards';
import { EnhancedTableHeadColumn } from 'dashboard409a/components/ValuationsTable/ValuationsTable.types';
import {
  getPreference,
  migrateLegacyLocalStorageKey,
  setPreference,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from 'utilities/logger';

const COLUMN_VISIBILITY_PREFIX = 'valuations_column_visibility_';

/**
 * Gets the storage key for a specific section
 */
const getStorageKey = (sectionId: string): string => `${COLUMN_VISIBILITY_PREFIX}${sectionId}`;

const buildDefaultVisibility = (columns: EnhancedTableHeadColumn[]): Record<string, boolean> => {
  const initialVisibility: Record<string, boolean> = {};
  columns.forEach(col => {
    initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
  });
  return initialVisibility;
};

const mergeStoredWithDefaults = (
  parsed: Record<string, boolean>,
  columns: EnhancedTableHeadColumn[]
): Record<string, boolean> => {
  const initialVisibility: Record<string, boolean> = {};
  columns.forEach(col => {
    if (parsed[col.id] !== undefined) {
      initialVisibility[col.id] = parsed[col.id];
    } else {
      initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
    }
  });
  return initialVisibility;
};

/**
 * Loads column visibility from IndexedDB (with legacy localStorage migration).
 */
export const loadColumnVisibilityFromStorage = async (
  sectionId: string,
  columns: EnhancedTableHeadColumn[]
): Promise<Record<string, boolean>> => {
  const storageKey = getStorageKey(sectionId);
  try {
    await migrateLegacyLocalStorageKey(storageKey);
    const stored = await getPreference<Record<string, boolean>>(storageKey);
    if (stored) {
      return mergeStoredWithDefaults(stored, columns);
    }
  } catch {
    // fall through to defaults
  }
  return buildDefaultVisibility(columns);
};

/**
 * @deprecated Prefer useLocalStorage in ValuationsTableBulkData; sync helper for initial render defaults only.
 */
export const getInitialColumnVisibilityFromStorage = (
  sectionId: string,
  columns: EnhancedTableHeadColumn[]
): Record<string, boolean> => buildDefaultVisibility(columns);

/**
 * Saves column visibility via the setValue function from useLocalStorage hook
 */
export const saveColumnVisibilityToStorage = (
  sectionId: string,
  columnVisibility: Record<string, boolean>,
  setValue: (value: Record<string, boolean>) => void
): void => {
  try {
    setValue(columnVisibility);
  } catch (error) {
    Logger.debug(`Error saving column visibility for ${sectionId}:`, error);
  }
};

export const persistColumnVisibility = async (
  sectionId: string,
  columnVisibility: Record<string, boolean>
): Promise<void> => {
  await setPreference(getStorageKey(sectionId), columnVisibility);
};
